import React, { useState, useEffect, useContext, useReducer } from "react";
import {
  Grid,
  Container,
  makeStyles,
  Collapse,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Typography,
  TablePagination,
} from "@material-ui/core";
import { Loader, SimpleToolbar, Drawer } from "../components";
import { laboratoryServices } from "../services";
import { AuthContext, BadgeContext } from "../context";
import { ReportsAvatarIcon } from "../icons";
import { useDrawer } from "../hooks";

export default function AlignItemsList() {
  const classes = useStyles();
  const [loading, setLoading] = useState(false);
  const [limit, setLimit] = useState(100);
  const [page, setPage] = useState(1);
  const [count, setCount] = useState(0);
  const [index, setIndex] = useState(null);
  const { authState, signOut } = useContext(AuthContext);
  const { setQuantity } = useContext(BadgeContext);
  const { isOpen, setIsOpen } = useDrawer();

  const [reports, dispatch] = useReducer((myArray, { type, value }) => {
    switch (type) {
      case "add":
        return [...myArray, value];
      case "remove":
        return myArray.filter((_, index) => index !== value);
      case "clean":
        return [];
      case "updateIsSelect":
        return myArray.map((element) => {
          if (element._id === value) {
            return {
              ...element,
              read: true,
            };
          }
          return { ...element };
        });
      default:
        return myArray;
    }
  }, []);

  useEffect(() => {
    getReports("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authState, page, limit]);

  const getReports = async (text) => {
    try {
      setLoading(true);
      const { status, response } = await laboratoryServices.getReports({
        laboratoryId: authState.user.laboratory
          ? authState.user.laboratory._id
          : null,
        page,
        limit,
        token: authState.token,
        text,
      });
      setLoading(false);
      if (status === 200) {
        dispatch({ type: "clean" });
        response.reports.forEach((element) =>
          dispatch({ type: "add", value: { ...element } })
        );
        setLimit(response.limit);
        setCount(response.total);
        setPage(response.page);
      } else if (status === 401) {
        signOut();
      }
    } catch (e) {
      setLoading(false);
      console.log(e);
    }
  };

  const onClickReport = async (_id) => {
    setIndex(index === _id ? null : _id);
    dispatch({ type: "updateIsSelect", value: _id });
    let element = reports.find((el) => el._id === _id);
    if (!element.read) {
      const { status, response } = await laboratoryServices.updateReport({
        _id,
        token: authState.token,
      });
      if (status === 200) {
        setQuantity({ quantity: response.count });
      } else if (status === 401) {
        signOut();
      }
    }
  };

  return (
    <>
      <Drawer open={isOpen} setOpen={(value) => setIsOpen(value)} />
      <Grid container className={classes.container} style={{ flex: 1 }}>
        {loading && <Loader loading={true} />}
        <Grid item sm={12} xs={12}>
          <Grid item sm={12}>
            <div className={classes.rootDiv}>
              <Container maxWidth={false}>
                <SimpleToolbar
                  isOpen={isOpen}
                  setIsOpen={(value) => setIsOpen(value)}
                  onFilter={(text) => getReports(text)}
                />
                <List className={classes.root}>
                  {reports.map((report, id) => (
                    <React.Fragment key={report._id}>
                      <ListItem
                        alignItems="flex-start"
                        onClick={() => onClickReport(report._id)}
                        style={{
                          backgroundColor: !report.read
                            ? "rgba(0, 0, 0, 0.12)"
                            : "unset",
                          cursor: "pointer",
                        }}
                      >
                        <ListItemAvatar>
                          <ReportsAvatarIcon />
                        </ListItemAvatar>
                        <ListItemText
                          className={classes.listItemText}
                          primary={
                            <a href={`mailto:${report.doctor.user.email}`}>
                              {report.doctor.user.email}
                            </a>
                          }
                          secondary={
                            <React.Fragment>
                              <Typography
                                component="span"
                                variant="body2"
                                className={classes.inline}
                                color="textPrimary"
                              >
                                {report.doctor.name} {report.doctor.lastName}
                              </Typography>
                              {" - "} {report.report}
                            </React.Fragment>
                          }
                        />
                      </ListItem>
                      <Collapse
                        in={report._id === index}
                        timeout="auto"
                        unmountOnExit
                        className={classes.collapse}
                      >
                        {report.report.split("\n").map((el, index) => (
                          <Typography
                            component="span"
                            variant="body2"
                            key={index}
                            className={classes.text}
                            color="textPrimary"
                          >
                            {el}
                            <br />
                          </Typography>
                        ))}
                      </Collapse>
                    </React.Fragment>
                  ))}
                </List>
                <TablePagination
                  labelRowsPerPage="Registros por página"
                  component="div"
                  count={count}
                  onChangeRowsPerPage={(e) => setLimit(e.target.value)}
                  onChangePage={(e, page) => setPage(page + 1)}
                  page={page - 1}
                  rowsPerPage={limit}
                  rowsPerPageOptions={[10, 25, 50, 100]}
                  classes={{ root: classes.pagination }}
                />
              </Container>
            </div>
          </Grid>
        </Grid>
      </Grid>
    </>
  );
}

const useStyles = makeStyles((theme) => ({
  root: {
    width: "100%",
    height: "calc(100vh - 270px)",
    margin: "20px 0 0 0",
    overflowY: "scroll",
    backgroundColor: theme.palette.background.paper,
  },
  rootDiv: {
    width: "100%",
    paddingBottom: theme.spacing(2),
    paddingTop: theme.spacing(2),
  },
  inline: {
    display: "inline",
  },
  listItemText: {
    display: "-webkit-box",
    maxWidth: "100%",
    overflow: "hidden",
    textOverflow: "ellipsis",
    "-webkit-line-clamp": 2,
    "-moz-box-orient": "vertical",
    "-webkit-box-orient": "vertical",
    "box-orient": "vertical",
  },
  container: {
    maxHeight: "100%",
    borderRadius: "22px 22px 22px 22px",
  },
  pagination: {
    color: theme.palette.secondary.main,
    [theme.breakpoints.down("xs")]: {
      position: "absolute",
      bottom: 0,
    },
  },
  collapse: {
    padding: "0 10px 10px 72px",
  },
}));
